/**
 * Small UPS descriptor-engine save bridge (#997 Story 6.4 Phase 4b) — the
 * Small UPS instantiation of the generic `useRepeatableEngineSaveBridge`
 * (mirror of `useRacksEngineSaveBridge`).
 *
 * Replaces the parent-owned `useSmallUpsEditSession` for the persisted (Request
 * Detail) path once the Small UPS step runs on `DescriptorEngineSection`.
 * Supplies the SMALL UPS bindings (query hook, flush, canonical content-key
 * comparator, invalidation keys, legacy synthesis, editedData key) and renames
 * the generic handle to the unchanged bespoke `SmallUpsConfigurationsSaveHandle`
 * contract so every existing call site keeps working. The flush's created-id map
 * is surfaced through `flushPendingSmallUpsChanges` for the Obs 6 staged-upload
 * remap.
 *
 * See `useRepeatableEngineSaveBridge` for the held-baseline / same-chain /
 * reset-to-flush-result semantics (the 6-3 review-patch cluster).
 */

import { useMemo } from 'react';

import {
  smallUpsConfigurationsKeys,
  useSmallUpsConfigurations,
} from './queries/useSmallUpsConfigurations';
import {
  useRepeatableEngineSaveBridge,
  type AggregateSaveBridgeExtras,
} from './useRepeatableEngineSaveBridge';
import { buildLegacySmallUpsFromRequestFields } from '../components/smallups/smallUpsListUtils';
import {
  flushSmallUpsEditSession,
  smallUpsEditSessionHasChanges,
} from '../services/smallUpsEditSessionFlush';

import type { RepeatableInstanceSaveHandle } from '../components/repeatableSections/repeatableInstanceSaveHandle';
import type { SmallUpsConfigurationsSaveHandle } from '../request/steps/smallups/subsections/smallUpsConfigurationsSaveHandle';
import type { SmallUpsConfiguration } from '../types/smallUps';

export type UseSmallUpsEngineSaveBridgeParams = {
  requestId: string | undefined;
  isEditing: boolean;
  isOfflineRequest: boolean;
  /** Flat request fields (request + editedData merge) — legacy unit synthesis. */
  legacyRequestFields?: Record<string, unknown>;
  /** The request payload's PERSISTED `smallUpsConfigurations` embed. */
  persistedEmbedSmallUps?: unknown;
  /** Live session list — `editedData.smallUpsConfigurations` (engine bridge writes it). */
  editedSmallUpsConfigurations?: unknown;
  /** True when Small UPS was touched this session (`dirtyFields.has('smallUpsConfigurations')`). */
  smallUpsConfigurationsDirty: boolean;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  restoredBaseline?: SmallUpsConfiguration[] | null;
  onDirtyChange?: (dirty: boolean) => void;
};

export function useSmallUpsEngineSaveBridge({
  requestId,
  isEditing,
  isOfflineRequest,
  legacyRequestFields,
  persistedEmbedSmallUps,
  editedSmallUpsConfigurations,
  smallUpsConfigurationsDirty,
  applyEditedDataPatch,
  restoredBaseline,
  onDirtyChange,
}: UseSmallUpsEngineSaveBridgeParams): {
  saveHandle:
    | (SmallUpsConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null;
  registerInstanceSaveHandle: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  /** The held edit-session baseline (for draft-snapshot persistence). */
  sessionBaseline: SmallUpsConfiguration[] | null;
} {
  const {
    saveHandle: genericHandle,
    registerInstanceSaveHandle,
    sessionBaseline,
  } = useRepeatableEngineSaveBridge<SmallUpsConfiguration>({
    requestId,
    isEditing,
    isOfflineRequest,
    legacyRequestFields,
    persistedEmbedInstances: persistedEmbedSmallUps,
    editedInstances: editedSmallUpsConfigurations,
    instancesDirty: smallUpsConfigurationsDirty,
    applyEditedDataPatch,
    restoredBaseline,
    onDirtyChange,
    editedDataKey: 'smallUpsConfigurations',
    useInstancesQuery: useSmallUpsConfigurations,
    hasChanges: smallUpsEditSessionHasChanges,
    flush: flushSmallUpsEditSession,
    invalidateListKeys: id => [
      smallUpsConfigurationsKeys.list(id),
      smallUpsConfigurationsKeys.grouped(id),
    ],
    buildLegacyFromRequestFields: buildLegacySmallUpsFromRequestFields,
  });

  const saveHandle = useMemo(():
    | (SmallUpsConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null => {
    if (!genericHandle) {
      return null;
    }
    return {
      isSmallUpsEditorOpen: genericHandle.isEditorOpen,
      hasUnsavedSmallUpsEditor: genericHandle.hasUnsavedEditor,
      commitOpenSmallUpsEditor: genericHandle.commitOpenEditor,
      saveUnsavedSmallUpsIfNeeded: genericHandle.saveUnsavedIfNeeded,
      hasPendingSmallUpsChanges: genericHandle.hasPendingChanges,
      flushPendingSmallUpsChanges: genericHandle.flushPendingChanges,
      tryDismissSmallUpsEditor: genericHandle.tryDismissEditor,
      discardOpenSmallUpsEditor: genericHandle.discardOpenEditor,
      // #1501 aggregate save seams (uniform names across all six families)
      getPendingDiff: genericHandle.getPendingDiff,
      absorbExternalFlushResult: genericHandle.absorbExternalFlushResult,
      remapSessionIds: genericHandle.remapSessionIds,
      resetSessionAfterScopeRemoval:
        genericHandle.resetSessionAfterScopeRemoval,
    };
  }, [genericHandle]);

  return { saveHandle, registerInstanceSaveHandle, sessionBaseline };
}
